import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Header from "./Header";
import Swal from "sweetalert2";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import { BASE_URL } from "../Apis/apiConfig";
import { Car, RefreshCw, AlertCircle,FileX,  Phone, User, Hash, MapPin,Trash2, CheckSquare, Search, Eye, Edit,MoreVertical,X,Download 
} from "lucide-react";

import * as XLSX from "xlsx";

const ParkingList = () => {
  const [parkingList, setParkingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const downloadExcel = () => {
  if (parkingList.length === 0) {
    Swal.fire("Info", "No parking records found", "info");
    return;
  }

  const excelData = parkingList.map((item, index) => ({
    "Sr No": index + 1,
    "Member Name": item.member_name || "",
    "Unit No": item.unit_no || "",
    "Number Plate": item.number_plate || "",
    "Vehicle": item.vehicle || "",
    "Phone Number": item.phone_number || "",
  }));

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Parking Records"
  );

  // Automatically downloads file
  XLSX.writeFile(
    workbook,
    `Parking_Records_${new Date().toISOString().split("T")[0]}.xlsx`
  );
};
  const getParkingData = async () => {
    setLoading(true);
    setError(null);
    try {
      const societyName = localStorage.getItem("society") ?? "";
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const response = await axios.post(
        `${BASE_URL}/credit/get-all-parking-by-society-name`,
        null,
        { params: { societyName }, headers },
      );

      const data = Array.isArray(response.data) ? response.data : [];
      setParkingList(data);
    } catch (err) {
      console.error("API error:", err);
      setError(err?.response?.data ?? err.message ?? "Failed to fetch");
      setParkingList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getParkingData();
  }, []);

  // Define column structure for parking list
  const columns = useMemo(
    () => [
      { key: "sr", label: "Sr", width: "60px", icon: null },
      {
        key: "member_name",
        label: "Member Name",
        width: "150px",
        icon: <User size={14} />,
      },
      {
        key: "unit_no",
        label: "Unit No",
        width: "100px",
        icon: <MapPin size={14} />,
      },
      {
        key: "number_plate",
        label: "Number Plate",
        width: "140px",
        icon: <Hash size={14} />,
      },
      {
        key: "vehicle",
        label: "Vehicle",
        width: "120px",
        icon: <Car size={14} />,
      },
      {
        key: "phone_number",
        label: "Phone Number",
        width: "130px",
        icon: <Phone size={14} />,
      },
    ],
    [],
  );

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return parkingList;

    const searchLower = searchTerm.toLowerCase();
    return parkingList.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchLower),
      ),
    );
  }, [parkingList, searchTerm]);

  // Pagination
  const totalItems = filteredData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / perPage));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [perPage, totalItems, currentPage, totalPages]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredData.slice(start, start + perPage);
  }, [filteredData, currentPage, perPage]);

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // Render page numbers
  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 5;

    if (totalPages <= maxButtons) {
      for (let p = 1; p <= totalPages; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }
    } else {
      let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
      let end = Math.min(totalPages, start + maxButtons - 1);

      if (end - start < maxButtons - 1) {
        start = Math.max(1, end - maxButtons + 1);
      }

      if (start > 1) {
        pages.push(
          <button
            key={1}
            className={`pagination-btn ${1 === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(1)}
          >
            1
          </button>,
        );
        if (start > 2) {
          pages.push(
            <span key="start-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
      }

      for (let p = start; p <= end; p++) {
        pages.push(
          <button
            key={p}
            className={`pagination-btn ${p === currentPage ? "active" : ""}`}
            onClick={() => handlePageChange(p)}
          >
            {p}
          </button>,
        );
      }

      if (end < totalPages) {
        if (end < totalPages - 1) {
          pages.push(
            <span key="end-ellipsis" className="pagination-ellipsis">
              ...
            </span>,
          );
        }
        pages.push(
          <button
            key={totalPages}
            className={`pagination-btn ${
              totalPages === currentPage ? "active" : ""
            }`}
            onClick={() => handlePageChange(totalPages)}
          >
            {totalPages}
          </button>,
        );
      }
    }

    return pages;
  };

  const deleteByAdminBillTable = async () => {
    const token = localStorage.getItem("token");
    const societyName = localStorage.getItem("society");
    if (!societyName) {
      Swal.fire("Error", "Society name is required", "error");
      return;
    }

    const result = await Swal.fire({
      title: "Are you absolutely sure?",
      text: `This will permanently delete ALL parking records for "${societyName}"!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete all",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.delete(
        `${BASE_URL}/parking/delete-table-by-admin`,
        {
          params: { societyName },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      Swal.fire(
        "Deleted!",
        response.data || "Parking records deleted successfully!",
        "success",
      );
      await getParkingData();
      setSelectedMembers([]);
    } catch (error) {
      console.error(error);
      Swal.fire(
        "Error",
        error.response?.data || "Failed to delete parking records",
        "error",
      );
    }
  };

  const deleteSelectedMembers = async () => {
    if (selectedMembers.length === 0) {
      Swal.fire("Info", "Please select parking records to delete", "info");
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `This will delete ${selectedMembers.length} selected parking record(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society") || "";
      const response = await axios.delete(
        `${BASE_URL}/parking/delete-table-by-admin-by-multile`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            societyName,
            multipleIds: selectedMembers.map(Number),
          },
        },
      );

      Swal.fire(
        "Deleted!",
        response.data || "Selected parking records deleted successfully",
        "success",
      );
      await getParkingData();
      setSelectedMembers([]);
    } catch (err) {
      console.error(err);
      Swal.fire(
        "Error",
        err.response?.data || "Failed to delete selected parking records",
        "error",
      );
    }
  };

  const selectAllRows = (e) => {
    if (e.target.checked) {
      const allIds = paginatedData
        .map((item) => item.id ?? item._id)
        .filter((id) => id !== undefined);
      setSelectedMembers(allIds);
    } else {
      setSelectedMembers([]);
    }
  };

  const handleRowSelect = (id) => {
    if (!id) return;

    setSelectedMembers((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const safe = (v) =>
    v !== undefined && v !== null && String(v).trim() !== "" ? String(v) : "-";

  return (
    <div className="app-container">
      <style>
        {`
/* ========== PARKING LIST PAGE OPTIMIZATIONS ========== */
.app-container {
  height: 100vh;
  overflow: hidden !important;
}

.main-layout {
  height: calc(100vh - 64px);
  overflow: hidden !important;
}

.content-area {
  height: 100%;
  overflow-y: auto !important;
  overflow-x: hidden;
  padding-bottom: 20px;
}

.financial-container,
.financial-card {
  height: auto !important;
  overflow: visible !important;
  margin: 0 !important;
  padding: 0 !important;
}

.financial-body {
  height: auto !important;
  overflow: visible !important;
  padding: 0 !important;
}

/* ========== COMPACT TABLE CONTROLS ========== */
.controls-left,
.controls-center,
.controls-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.controls-center {
  flex: 1;
  justify-content: center;
}

.entries-selector label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

.entries-select {
  padding: 4px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  background: white;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  height: 28px;
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  border: none;
}

.pagination-nav {
  padding: 4px 10px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  height: 26px;
  transition: all 0.2s;
}

.pagination-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.pagination-nav:not(:disabled):hover {
  background: #f1f5f9;
}

.pagination-btn {
  min-width: 26px;
  height: 26px;
  padding: 4px 8px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  cursor: pointer;
  color: #334155;
  font-size: 11px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination-btn:hover {
  background: #f1f5f9;
}

.pagination-btn.active {
  background: #0d6efd;
  color: white;
  border-color: #0d6efd;
}

.pagination-ellipsis {
  padding: 0 4px;
  color: #94a3b8;
  font-size: 11px;
}

.entries-info {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

/* ========== TABLE OPTIMIZATION ========== */
.financial-table-container {
  overflow-x: auto;
  padding: 0;
  margin: 0;
}

.financial-table,
.data-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
}

.financial-table th,
.data-table th {
  padding: 6px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  white-space: nowrap;
  font-weight: 600;
  font-size: 11px !important;
  background-color: #f8fafc;
  border-bottom: 2px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.2 !important;
  height: 32px !important;
}

.financial-table td,
.data-table td {
  padding: 5px 8px !important;
  text-align: center !important;
  vertical-align: middle;
  font-size: 10px !important;
  border-bottom: 1px solid #e2e8f0;
  border-right: 1px solid #e2e8f0;
  line-height: 1.3 !important;
  height: 36px !important;
}

/* Checkbox Column */
.financial-table th:nth-child(1),
.financial-table td:nth-child(1),
.data-table th:first-child,
.data-table td:first-child {
  width: 35px !important;
  min-width: 35px !important;
  max-width: 35px !important;
  padding: 5px 2px !important;
}

/* Actions Column */
.financial-table th:last-child,
.financial-table td:last-child,
.data-table th:last-child,
.data-table td:last-child {
  width: 120px !important;
  min-width: 120px !important;
  padding: 4px 2px !important;
}

/* Action Buttons */
.action-buttons {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin: 0;
}

.action-icon-btn,
.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 3px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  width: 24px;
  height: 24px;
}

.action-icon-btn svg,
.icon-btn svg {
  width: 14px;
  height: 14px;
}

.action-icon-btn:hover,
.icon-btn:hover {
  background-color: #f1f5f9;
}

.icon-btn.edit {
  color: #0d6efd;
}

/* Checkbox */
.financial-table input[type="checkbox"],
.data-table input[type="checkbox"] {
  margin: 0;
  width: 13px;
  height: 13px;
  vertical-align: middle;
  cursor: pointer;
}

/* Row Hover */
.financial-table tbody tr:hover td,
.data-table tbody tr:hover td {
  background-color: #f8fafc;
}

.selected-row td {
  background-color: #e3f2fd !important;
}

/* Cell Content */
.cell-content {
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

/* Empty State */
.financial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  min-height: 300px;
}

.financial-state h3 {
  margin: 16px 0 8px 0;
  color: #334155;
  font-size: 18px;
  font-weight: 600;
}

.financial-state p {
  color: #64748b;
  font-size: 14px;
  margin: 0;
}

/* Loading Animation */
.spin-animation {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Responsive */
@media (max-width: 768px) {
  .controls-left,
  .controls-center,
  .controls-right {
    width: 100%;
    justify-content: space-between;
  }
}

/* Specific for parking list table */
.parking-table-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.column-header svg {
  flex-shrink: 0;
}

/* Member name styling */
.member-info strong {
  color: #087db7;
  font-size: 11px;
}

/* Phone info styling */
.phone-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.phone-info svg {
  flex-shrink: 0;
}

/* Number plate badge */
.number-plate-badge {
  display: inline-block;
  padding: 2px 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  font-family: monospace;
  font-size: 10px;
  font-weight: 600;
}

/* Compact row height for parking */
.reduceHeight {
  height: 36px !important;
  line-height: 1.2 !important;
}

.reduceHeight td {
  padding: 5px 6px !important;
  font-size: 10px !important;
  vertical-align: middle !important;
}

.reduceHeight .cell-content {
  max-height: 24px !important;
  overflow: hidden !important;
  text-align: center !important;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.reduceHeight .icon-btn {
  padding: 2px !important;
  width: 24px !important;
  height: 24px !important;
}

.reduceHeight .icon-btn svg {
  width: 12px !important;
  height: 12px !important;
}

.reduceHeight input[type="checkbox"] {
  width: 12px !important;
  height: 12px !important;
}

/* Actions dropdown for mobile */
.actions-dropdown {
  position: relative;
}

.actions-dropdown-menu {
  position: absolute;
  right: 0;
  top: 100%;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  z-index: 10;
  min-width: 120px;
}

.actions-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  width: 100%;
  background: none;
  border: none;
  text-align: left;
  font-size: 11px;
  cursor: pointer;
}

.actions-dropdown-item:hover {
  background: #f1f5f9;
}

.actions-dropdown-item.danger {
  color: #dc2626;
}
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="financial-container">
            <div className="financial-card">
              {/* SINGLE COMPACT CONTROL ROW - Matching Receipt.js */}
              <div className="flex px-4 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-tr-lg rounded-tl-lg text-white py-2">
                {/* Left - Entries Selector */}
                <div className="controls-left">
                  <div className="entries-selector">
                    <label className="text-white">
                      Show
                      <select
                        value={perPage}
                        onChange={handlePerPageChange}
                        className="entries-select"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                      entries
                    </label>
                  </div>
                </div>

                {/* Center - Search and Action Buttons */}
                <div className="controls-center">
                  <div className="flex items-center gap-2">
                    {/* Search Input - Compact */}
                    <div className="relative">
                      <Search
                        size={14}
                        className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400"
                      />
                      <input
                        type="text"
                        placeholder="Search parking records..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setCurrentPage(1);
                        }}
                        className="pl-8 pr-8 py-1 border border-gray-300 rounded text-sm text-gray-700"
                        style={{
                          width: "200px",
                          height: "28px",
                          fontSize: "12px",
                        }}
                      />
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2"
                        >
                          <X
                            size={14}
                            className="text-gray-400 hover:text-gray-600"
                          />
                        </button>
                      )}
                    </div>

                    {/* Action Buttons - Compact */}
                    <button
                      className="rounded border-none flex bg-blue-300 text-white hover:bg-blue-700 p-2"
                      onClick={getParkingData}
                      disabled={loading}
                      title="Refresh parking records"
                    >
                      <RefreshCw
                        size={14}
                        className={loading ? "spin-animation" : ""}
                      />
                    </button>

                    <button
                      className="rounded border-none flex align-center bg-red-300 text-white hover:bg-red-500 p-2"
                      onClick={deleteByAdminBillTable}
                      title="Delete all parking records"
                    >
                      <Trash2 size={14} />
                    </button>

                    <button
                      className="rounded border-none gap-2 flex items-center text-left bg-red-300 text-white hover:bg-red-500 px-2 py-1"
                      onClick={deleteSelectedMembers}
                      disabled={selectedMembers.length === 0}
                      title={`Delete ${selectedMembers.length} selected parking record(s)`}
                    >
                      <Trash2 size={14} />
                      <span style={{ fontSize: "11px" }}>
                        {selectedMembers.length}
                      </span>
                      <CheckSquare size={14} />
                    </button>
                  </div>
                </div>

                {/* Right - Pagination */}
                <div className="controls-right">
                  {!loading && totalItems > 0 && (
                    <span className="entries-info text-white">
                      {(currentPage - 1) * perPage + 1}-
                      {Math.min(currentPage * perPage, totalItems)} of{" "}
                      {totalItems}
                    </span>
                  )}

                  <div className="flex gap-2 p-2">
                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Prev
                    </button>

                    {renderPageNumbers()}

                    <button
                      className="pagination-nav"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
                <button
  className="rounded border-none flex items-center gap-1 bg-green-500 text-white hover:bg-green-600 px-3 py-2"
  onClick={downloadExcel}
  title="Download Excel"
>
  <Download size={14} />
  <span style={{ fontSize: "11px" }}></span>
</button>
              </div>

              {/* Main Content Area */}
              <div className="financial-body">
                {loading ? (
                  <div className="financial-state">
                    <RefreshCw size={48} className="spin-animation" />
                    <h3>Loading Parking Records</h3>
                    <p>Please wait while we fetch the data...</p>
                  </div>
                ) : error ? (
                  <div className="financial-state error-state">
                    <AlertCircle size={48} />
                    <h3>Unable to Load Data</h3>
                    <p>{String(error)}</p>
                    <button
                      onClick={getParkingData}
                      style={{
                        marginTop: "20px",
                        padding: "10px 20px",
                        background: "#0d6efd",
                        color: "white",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Try Again
                    </button>
                  </div>
                ) : parkingList.length === 0 ? (
                  <div className="financial-state">
                    <div
                      style={{
                        background: "#f8fafc",
                        padding: "40px",
                        borderRadius: "16px",
                        textAlign: "center",
                        maxWidth: "500px",
                        margin: "0 auto",
                      }}
                    >
                      <FileX size={64} color="#94a3b8" />
                      <h3
                        style={{
                          margin: "20px 0 10px 0",
                          color: "#334155",
                          fontSize: "20px",
                          fontWeight: "600",
                        }}
                      >
                        No Parking Records Found
                      </h3>
                      <p
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          margin: 0,
                          lineHeight: "1.5",
                        }}
                      >
                        {searchTerm
                          ? "No parking records match your search criteria."
                          : "There are no parking records available for this society."}
                      </p>
                      {searchTerm && (
                        <button
                          onClick={() => setSearchTerm("")}
                          style={{
                            marginTop: "20px",
                            padding: "8px 20px",
                            background: "#f1f5f9",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            color: "#475569",
                            cursor: "pointer",
                            fontSize: "14px",
                          }}
                        >
                          Clear Search
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="financial-table-container">
                    <table
                      className="data-table compact-table"
                      style={{ minWidth: "1000px" }}
                    >
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              onChange={selectAllRows}
                              checked={
                                selectedMembers.length > 0 &&
                                selectedMembers.length ===
                                  paginatedData.filter(
                                    (item) => item.id || item._id,
                                  ).length
                              }
                            />
                          </th>
                          {columns.map((col) => (
                            <th
                              key={col.key}
                              style={{ width: col.width }}
                              className="border"
                            >
                              <div className="column-header">
                                {col.icon}
                                <span>{col.label}</span>
                              </div>
                            </th>
                          ))}
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((item, idx) => {
                          const itemId = item.id ?? item._id;
                          const srNo =
                            item.sr || idx + 1 + (currentPage - 1) * perPage;

                          return (
                            <tr
                              key={itemId ?? idx}
                              className={`reduceHeight ${
                                selectedMembers.includes(itemId)
                                  ? "selected-row"
                                  : ""
                              }`}
                            >
                              <td>
                                {itemId && (
                                  <input
                                    type="checkbox"
                                    checked={selectedMembers.includes(itemId)}
                                    onChange={() => handleRowSelect(itemId)}
                                  />
                                )}
                              </td>
                              <td
                                className="border"
                                style={{
                                  textAlign: "center",
                                  color: "#6b7280",
                                }}
                              >
                                {srNo}
                              </td>
                              <td className="border">
                                <div className="member-info">
                                  <strong style={{ color: "#087db7" }}>
                                    {safe(item.member_name)}
                                  </strong>
                                </div>
                              </td>
                              <td className="border">
                                <div className="wing-info">
                                  <strong>{safe(item.unit_no)}</strong>
                                </div>
                              </td>
                              <td className="border">
                                <span className="number-plate-badge">
                                  {safe(item.number_plate)}
                                </span>
                              </td>
                              <td className="border">
                                <div className="vehicle-info">
                                  {safe(item.vehicle)}
                                </div>
                              </td>
                              <td className="border">
                                <div className="phone-info">
                                  <Phone size={12} />
                                  <span>{safe(item.phone_number)}</span>
                                </div>
                              </td>
                              <td>
                                <div className="action-buttons">
                                  {/* Desktop view */}
                                  <div className="d-none d-md-flex gap-1">
                                    <button
                                      className="icon-btn edit"
                                      title="View Details"
                                      onClick={() => {
                                        Swal.fire({
                                          title: "Parking Record Details",
                                          html: `
                                            <div style="text-align: left; font-family: monospace;">
                                              <div style="margin-bottom: 8px;">
                                                <strong>Member Name:</strong> ${safe(item.member_name)}
                                              </div>
                                              <div style="margin-bottom: 8px;">
                                                <strong>Unit No:</strong> ${safe(item.unit_no)}
                                              </div>
                                              <div style="margin-bottom: 8px;">
                                                <strong>Number Plate:</strong> ${safe(item.number_plate)}
                                              </div>
                                              <div style="margin-bottom: 8px;">
                                                <strong>Vehicle:</strong> ${safe(item.vehicle)}
                                              </div>
                                              <div style="margin-bottom: 8px;">
                                                <strong>Phone Number:</strong> ${safe(item.phone_number)}
                                              </div>
                                            </div>
                                          `,
                                          width: "500px",
                                          confirmButtonText: "Close",
                                        });
                                      }}
                                    >
                                      <Eye size={14} />
                                    </button>
                                    <button
                                      className="icon-btn edit"
                                      title="Edit Record"
                                      onClick={() => {
                                        Swal.fire(
                                          "Info",
                                          "Edit parking record functionality coming soon",
                                          "info",
                                        );
                                      }}
                                    >
                                      <Edit size={14} />
                                    </button>
                                  </div>

                                  {/* Mobile view */}
                                  <div className="d-md-none actions-dropdown">
                                    <button
                                      className="icon-btn edit"
                                      onClick={() =>
                                        setExpandedRow(
                                          expandedRow === idx ? null : idx,
                                        )
                                      }
                                      title="More Actions"
                                    >
                                      <MoreVertical size={14} />
                                    </button>
                                    {expandedRow === idx && (
                                      <div className="actions-dropdown-menu">
                                        <button
                                          className="actions-dropdown-item"
                                          onClick={() => {
                                            Swal.fire({
                                              title: "Parking Record Details",
                                              html: `
                                                <div style="text-align: left; font-family: monospace;">
                                                  <div style="margin-bottom: 8px;">
                                                    <strong>Member Name:</strong> ${safe(item.member_name)}
                                                  </div>
                                                  <div style="margin-bottom: 8px;">
                                                    <strong>Unit No:</strong> ${safe(item.unit_no)}
                                                  </div>
                                                  <div style="margin-bottom: 8px;">
                                                    <strong>Number Plate:</strong> ${safe(item.number_plate)}
                                                  </div>
                                                  <div style="margin-bottom: 8px;">
                                                    <strong>Vehicle:</strong> ${safe(item.vehicle)}
                                                  </div>
                                                  <div style="margin-bottom: 8px;">
                                                    <strong>Phone Number:</strong> ${safe(item.phone_number)}
                                                  </div>
                                                </div>
                                              `,
                                              width: "500px",
                                              confirmButtonText: "Close",
                                            });
                                          }}
                                        >
                                          <Eye size={14} /> View
                                        </button>
                                        <button
                                          className="actions-dropdown-item"
                                          onClick={() => {
                                            Swal.fire(
                                              "Info",
                                              "Edit parking record functionality coming soon",
                                              "info",
                                            );
                                          }}
                                        >
                                          <Edit size={14} /> Edit
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ParkingList;
